import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { useLanguage } from '../../contexts/LanguageContext';
import { Bot, Loader2, CheckCircle2, XCircle, Gauge, RefreshCw, ChevronDown, ChevronUp, ImageIcon, Play } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AiVisibilityCard() {
    const { language } = useLanguage();
    const de = language === 'de';
    const [data, setData] = useState(null);
    const [running, setRunning] = useState(false);
    const [open, setOpen] = useState(false);

    const load = useCallback(() => {
        axios.get(`${API}/seo/ai-visibility`).then((r) => setData(r.data)).catch(() => {});
    }, []);
    useEffect(() => { load(); }, [load]);

    const run = async () => {
        setRunning(true);
        try {
            await axios.post(`${API}/seo/ai-visibility/run`);
            // poll until finished (runs in background, ~1-2 min)
            for (let i = 0; i < 36; i++) {
                await new Promise((res) => setTimeout(res, 5000));
                const r = await axios.get(`${API}/seo/ai-visibility`);
                if (r.data.status === 'done') {
                    setData(r.data);
                    toast.success(de ? `Check fertig: ${r.data.mentions}/${r.data.total} Antworten nennen BESTBUDDY` : `Done: ${r.data.mentions}/${r.data.total} answers mention BESTBUDDY`);
                    setRunning(false);
                    return;
                }
                if (r.data.status === 'error') {
                    toast.error(de ? 'Check fehlgeschlagen – bitte erneut versuchen' : 'Check failed – please retry');
                    setRunning(false);
                    return;
                }
            }
            toast.error(de ? 'Zeitüberschreitung – später erneut prüfen' : 'Timeout – check again later');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Fehler');
        } finally {
            setRunning(false);
        }
    };

    return (
        <Card className="border-2 border-dashed border-sky-300 bg-sky-50/40" data-testid="ai-visibility-card">
            <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <Bot className="w-5 h-5 text-sky-600" />
                        <span className="text-sm font-bold">{de ? 'KI-Sichtbarkeit (ChatGPT & Co.)' : 'AI Visibility (ChatGPT & co.)'}</span>
                    </div>
                    <Button size="sm" variant="outline" onClick={run} disabled={running} className="h-8 gap-1.5 text-xs border-sky-300" data-testid="ai-visibility-run-btn">
                        {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                        {running ? (de ? 'Läuft (~1 Min)...' : 'Running (~1 min)...') : (de ? 'Check starten' : 'Run check')}
                    </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                    {de ? 'Nanouk stellt KI-Assistenten typische Nutzerfragen und prüft, ob sie BESTBUDDY empfehlen. Lücken werden automatisch Blog-Themen. Läuft zusätzlich monatlich automatisch.' : 'Nanouk asks AI assistants typical user questions and checks whether they recommend BESTBUDDY. Gaps automatically become blog topics. Also runs monthly.'}
                </p>
                {data?.score !== null && data?.score !== undefined && (
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <span className="text-2xl font-black text-sky-700" data-testid="ai-visibility-score">{data.score}%</span>
                            <span className="text-xs text-muted-foreground">
                                {data.mentions}/{data.total} {de ? 'KI-Antworten nennen dich' : 'AI answers mention you'} · {data.date && new Date(data.date).toLocaleDateString(de ? 'de-DE' : 'en-US')}
                            </span>
                        </div>
                        <button onClick={() => setOpen(!open)} className="text-xs font-semibold text-sky-700 inline-flex items-center gap-1" data-testid="ai-visibility-toggle">
                            {de ? 'Details je Frage' : 'Details per question'} {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        </button>
                        {open && (
                            <div className="space-y-1.5">
                                {(data.results || []).map((r, i) => (
                                    <div key={i} className="flex items-start gap-2 rounded-lg bg-white border border-sky-100 p-2">
                                        {r.mentioned ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />}
                                        <div className="min-w-0">
                                            <p className="text-[11px] font-semibold leading-snug">{r.question}</p>
                                            <p className="text-[10px] text-muted-foreground">{r.model}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

export function CitabilityCard() {
    const { language } = useLanguage();
    const de = language === 'de';
    const [posts, setPosts] = useState([]);
    const [busy, setBusy] = useState(null);
    const [openSlug, setOpenSlug] = useState(null);

    const load = useCallback(() => {
        axios.get(`${API}/blog/studio/citability`).then((r) => setPosts(r.data.posts || [])).catch(() => {});
    }, []);
    useEffect(() => { load(); }, [load]);

    const check = async (slug) => {
        setBusy(slug);
        try {
            const r = await axios.post(`${API}/blog/studio/citability/${slug}`);
            toast.success(de ? `Zitierbarkeit: ${r.data.score}/100` : `Citability: ${r.data.score}/100`);
            setOpenSlug(slug);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Fehler');
        } finally {
            setBusy(null);
        }
    };

    if (posts.length === 0) return null;

    return (
        <Card className="border-2 border-dashed border-amber-300 bg-amber-50/40" data-testid="citability-card">
            <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                    <Gauge className="w-5 h-5 text-amber-600" />
                    <span className="text-sm font-bold">{de ? 'Zitierbarkeits-Check (Artikel)' : 'Citability Check (articles)'}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                    {de ? 'Wie gern zitieren KI-Suchmaschinen deine Artikel? Prüfe je Artikel und erhalte konkrete Verbesserungen.' : 'How likely are AI search engines to cite your articles? Check each one and get concrete improvements.'}
                </p>
                <div className="space-y-1.5 max-h-72 overflow-y-auto">
                    {posts.map((p) => (
                        <div key={p.slug} className="rounded-lg bg-white border border-amber-100 p-2" data-testid={`citability-row-${p.slug}`}>
                            <div className="flex items-center justify-between gap-2">
                                <p className="text-xs font-semibold truncate min-w-0">{p.title}</p>
                                <div className="flex items-center gap-2 shrink-0">
                                    {p.citability && (
                                        <button onClick={() => setOpenSlug(openSlug === p.slug ? null : p.slug)}
                                            className={`text-xs font-black ${p.citability.score >= 70 ? 'text-emerald-600' : p.citability.score >= 45 ? 'text-amber-600' : 'text-red-500'}`}
                                            data-testid={`citability-score-${p.slug}`}>
                                            {p.citability.score}
                                        </button>
                                    )}
                                    <Button size="sm" variant="outline" onClick={() => check(p.slug)} disabled={busy === p.slug}
                                        className="h-6 px-2 text-[10px] border-amber-300" data-testid={`citability-check-${p.slug}`}>
                                        {busy === p.slug ? <Loader2 className="w-3 h-3 animate-spin" /> : (p.citability ? (de ? 'Neu' : 'Redo') : 'Check')}
                                    </Button>
                                </div>
                            </div>
                            {openSlug === p.slug && p.citability && (
                                <div className="mt-2 space-y-1 text-[11px]" data-testid={`citability-details-${p.slug}`}>
                                    {(p.citability.strengths || []).map((s, i) => <p key={`s${i}`} className="text-emerald-700">✓ {s}</p>)}
                                    {(p.citability.improvements || []).map((s, i) => <p key={`i${i}`} className="text-amber-700">→ {s}</p>)}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </CardContent>
        </Card>
    );
}

export function CoverBatchCard() {
    const { language } = useLanguage();
    const de = language === 'de';
    const [status, setStatus] = useState(null);
    const [starting, setStarting] = useState(false);

    const load = useCallback(() => {
        axios.get(`${API}/blog/studio/cover-batch`).then((r) => setStatus(r.data)).catch(() => {});
    }, []);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (status?.status !== 'running') return undefined;
        const id = setInterval(load, 5000);
        return () => clearInterval(id);
    }, [status?.status, load]);

    const start = async () => {
        setStarting(true);
        try {
            const r = await axios.post(`${API}/blog/studio/cover-batch`);
            if (r.data.total === 0) {
                toast.info(de ? 'Alle Artikel haben bereits ein Titelbild 🎉' : 'All articles already have a cover image 🎉');
            } else {
                toast.success(de ? `Batch gestartet: ${r.data.total} Artikel` : `Batch started: ${r.data.total} articles`);
            }
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Fehler');
        } finally {
            setStarting(false);
        }
    };

    const running = status?.status === 'running';
    const total = status?.total || 0;
    const processed = (status?.done || 0) + (status?.failed || 0);
    const progress = running && total ? Math.round((processed / total) * 100) : 0;
    const missing = status?.missing ?? null;

    return (
        <Card className="border-2 border-dashed border-rose-300 bg-rose-50/40" data-testid="cover-batch-card">
            <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <ImageIcon className="w-5 h-5 text-rose-600" />
                        <span className="text-sm font-bold">{de ? 'KI-Titelbilder (Batch)' : 'AI Cover Images (Batch)'}</span>
                    </div>
                    <Button size="sm" variant="outline" onClick={start} disabled={running || starting || missing === 0} className="h-8 gap-1.5 text-xs border-rose-300" data-testid="cover-batch-start-btn">
                        {running || starting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                        {running ? (de ? 'Läuft...' : 'Running...') : (de ? 'Batch starten' : 'Start batch')}
                    </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                    {de
                        ? `Generiert automatisch einzigartige KI-Titelbilder für alle veröffentlichten Artikel ohne Bild. Läuft im Hintergrund – du kannst die Seite verlassen.${missing !== null ? ` Aktuell ohne Bild: ${missing} Artikel.` : ''}`
                        : `Automatically generates unique AI cover images for all published articles without one. Runs in the background – you can leave the page.${missing !== null ? ` Currently missing: ${missing} articles.` : ''}`}
                </p>
                {running && (
                    <div className="space-y-1.5" data-testid="cover-batch-progress">
                        <div className="h-2 rounded-full bg-rose-100 overflow-hidden">
                            <div className="h-full bg-rose-500 transition-all duration-500" style={{ width: `${progress}%` }} />
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                            {processed}/{total} · {status?.current ? `${de ? 'Gerade' : 'Now'}: ${status.current}` : '...'}
                            {status?.failed > 0 && ` · ${status.failed} ${de ? 'fehlgeschlagen' : 'failed'}`}
                        </p>
                    </div>
                )}
                {status?.status === 'done' && (
                    <p className="text-xs font-semibold text-emerald-700" data-testid="cover-batch-done">
                        ✓ {de ? 'Letzter Batch:' : 'Last batch:'} {status.done} {de ? 'Bilder erstellt' : 'images created'}
                        {status.failed > 0 && `, ${status.failed} ${de ? 'fehlgeschlagen' : 'failed'}`}
                        {status.finished_at && ` · ${new Date(status.finished_at).toLocaleString(de ? 'de-DE' : 'en-US')}`}
                    </p>
                )}
            </CardContent>
        </Card>
    );
}

export function CoverGalleryCard() {
    const { language } = useLanguage();
    const de = language === 'de';
    const [posts, setPosts] = useState([]);
    const [busySlug, setBusySlug] = useState(null);
    const [open, setOpen] = useState(false);
    const [style, setStyle] = useState('auto');
    const BASE = process.env.REACT_APP_BACKEND_URL;

    const STYLES = [
        { id: 'auto', de: 'Auto', en: 'Auto' },
        { id: 'photorealistic', de: 'Fotorealistisch', en: 'Photorealistic' },
        { id: 'illustration', de: 'Illustration', en: 'Illustration' },
        { id: 'minimalist', de: 'Minimalistisch', en: 'Minimalist' },
        { id: 'watercolor', de: 'Aquarell', en: 'Watercolor' },
    ];

    const load = useCallback(() => {
        axios.get(`${API}/blog/posts?language=de&limit=100`).then((r) => setPosts(r.data.posts || [])).catch(() => {});
    }, []);
    useEffect(() => { load(); }, [load]);

    const regen = async (slug) => {
        setBusySlug(slug);
        try {
            const r = await axios.post(`${API}/blog/studio/cover/${slug}`, { style: style === 'auto' ? null : style });
            setPosts((prev) => prev.map((p) => p.slug === slug ? { ...p, image_url: r.data.image_url } : p));
            toast.success(de ? 'Neues Titelbild erstellt' : 'New cover image created');
        } catch (e) {
            toast.error(e?.response?.data?.detail || (de ? 'Fehlgeschlagen – bitte erneut versuchen' : 'Failed – please retry'));
        } finally {
            setBusySlug(null);
        }
    };

    return (
        <Card className="border-2 border-dashed border-teal-300 bg-teal-50/40" data-testid="cover-gallery-card">
            <CardContent className="p-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <ImageIcon className="w-5 h-5 text-teal-600" />
                        <span className="text-sm font-bold">{de ? 'Titelbild-Galerie' : 'Cover Gallery'}</span>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => setOpen(!open)} className="h-8 gap-1.5 text-xs border-teal-300" data-testid="cover-gallery-toggle">
                        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                        {open ? (de ? 'Zuklappen' : 'Collapse') : (de ? `Anzeigen (${posts.length})` : `Show (${posts.length})`)}
                    </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                    {de
                        ? 'Gefällt dir ein Titelbild nicht? Tippe auf das Bild und Nanouk generiert in ~15 Sekunden ein neues.'
                        : "Don't like a cover? Tap the image and Nanouk generates a new one in ~15 seconds."}
                </p>
                {open && (
                    <>
                        <div className="flex flex-wrap items-center gap-1.5" data-testid="cover-style-picker">
                            <span className="text-[10px] font-semibold text-muted-foreground">{de ? 'Stil:' : 'Style:'}</span>
                            {STYLES.map((s) => (
                                <button
                                    key={s.id}
                                    onClick={() => setStyle(s.id)}
                                    className={`text-[10px] px-2 py-0.5 rounded-full border transition-colors ${style === s.id ? 'bg-teal-600 text-white border-teal-600' : 'bg-card border-border text-muted-foreground hover:border-teal-300'}`}
                                    data-testid={`cover-style-${s.id}`}
                                >
                                    {de ? s.de : s.en}
                                </button>
                            ))}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-96 overflow-y-auto pr-1" data-testid="cover-gallery-grid">
                        {posts.map((p) => (
                            <button
                                key={p.slug}
                                onClick={() => busySlug === null && regen(p.slug)}
                                disabled={busySlug !== null}
                                className="relative group rounded-lg overflow-hidden border border-teal-100 bg-white text-left disabled:opacity-70"
                                data-testid={`cover-regen-${p.slug}`}
                                title={de ? 'Klicken = neues Bild generieren' : 'Click = generate new image'}
                            >
                                {p.image_url ? (
                                    <img src={`${BASE}${p.image_url}`} alt={p.title} loading="lazy" className="w-full h-20 object-cover" />
                                ) : (
                                    <div className="w-full h-20 bg-muted flex items-center justify-center">
                                        <ImageIcon className="w-5 h-5 text-muted-foreground" />
                                    </div>
                                )}
                                <div className={`absolute inset-0 flex items-center justify-center transition-opacity ${busySlug === p.slug ? 'bg-black/50 opacity-100' : 'bg-black/40 opacity-0 group-hover:opacity-100'}`}>
                                    {busySlug === p.slug
                                        ? <Loader2 className="w-5 h-5 text-white animate-spin" />
                                        : <RefreshCw className="w-5 h-5 text-white" />}
                                </div>
                                <p className="px-1.5 py-1 text-[10px] leading-tight text-muted-foreground truncate">{p.title}</p>
                            </button>
                        ))}
                    </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
